import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import ErrorState from '../../../commodity/components/ui/ErrorState';
import { formatMoney } from '../../../commodity/format/money';

import {
  AttentionItem,
  getTeacherDashboard,
  TeacherDashboard as DashboardModel,
} from '../api/teacher-dashboard';
import ApplicationStatusBanner from '../components/ApplicationStatusBanner';
import PendingEnrollmentRequests from '../components/PendingEnrollmentRequests';
import { useTeacherApplication } from '../hooks/useTeacherApplication';
import { MetricCard, MetricGrid, PageHeader, PortalPage } from '../../../design-system/portal';

/** Which panels could not be loaded, so the screen can say so (S1.3). */
interface Failures {
  dashboard?: unknown;
}

const PANEL_LABELS: Record<keyof Failures, string> = {
  dashboard: 'tu panel',
};

/**
 * TCH-07 — the teacher's home (story S5.1).
 *
 * Every figure here now comes from one server-computed payload. It used to be
 * assembled in the browser from three unrelated calls, which meant nobody
 * could check the numbers and at least two of them were wrong: "inscripciones
 * activas" counted every row the earnings endpoint returned including
 * cancelled ones, and it counted enrollments rather than children, so one
 * child on two courses showed as two students.
 *
 * Each panel still degrades on its own — one failing call leaves the rest
 * usable, which matters because this is the teacher's landing route. Degrading
 * is not the same as pretending, though, and it used to be: every rejection
 * became an empty value, so an outage rendered as a teacher who had never
 * taught and the advice below was then built on it.
 */
const TeacherDashboard: React.FC = () => {
  const { state: appState, reason } = useTeacherApplication();

  const [dashboard, setDashboard] = useState<DashboardModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [failures, setFailures] = useState<Failures>({});

  const load = useCallback(async () => {
    setLoading(true);
    const [dashboardRes] = await Promise.allSettled([getTeacherDashboard()]);
    setDashboard(dashboardRes.status === 'fulfilled' ? dashboardRes.value : null);
    setFailures({
      dashboard: dashboardRes.status === 'rejected' ? dashboardRes.reason : undefined,
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await load();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  if (loading) return <p>Cargando tu panel...</p>;

  const failed = (Object.keys(PANEL_LABELS) as Array<keyof Failures>).filter((k) => failures[k]);
  const allFailed = failed.length === Object.keys(PANEL_LABELS).length;

  const metrics = dashboard?.metrics;
  const currency = dashboard?.currency;

  // The list is computed server-side now (S5.1 / TCH-07-FR-09). It used to be
  // derived here from the three things the browser happened to have, which is
  // why it never mentioned a session about to start, a slot waiting on the
  // teacher, or a rubric holding their own money in escrow — the calendar,
  // the booking states and the evidence rules were all there, and nothing had
  // asked them.
  const attention = dashboard?.needsAttention ?? [];

  const today = new Date().toLocaleDateString('es-MX', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    /* The prototype scopes this screen's styles to its own element — the rules
       are written `#s_dashboard .mc`, not `.mc` — so the id is what makes the
       design apply rather than just the tokens. Same for `.layout > .main`,
       which sets the column width the whole portal shares. */
    <PortalPage screenId="s_dashboard">
      <PageHeader title="Mi panel" subtitle={today} />

      {failed.length > 0 && (
        <div style={{ margin: '16px 0' }}>
          <ErrorState
            action={
              allFailed
                ? 'cargar tu panel'
                : `cargar ${failed.map((k) => PANEL_LABELS[k]).join(' y ')}`
            }
            error={failures[failed[0]]}
            onRetry={load}
          />
          {!allFailed && (
            <p style={{ color: '#666', fontSize: 13, marginTop: 8 }}>
              Lo demás que ves abajo sí está actualizado.
            </p>
          )}
        </div>
      )}

      <ApplicationStatusBanner state={appState} reason={reason} />

      {/* D-P07-03: requests whose card is authorized and waiting on this teacher. */}
      <PendingEnrollmentRequests />

      <MetricGrid>
        <MetricCard
          label="Alumnos"
          value={metrics ? String(metrics.totalStudents) : '—'}
          to="/teacher/courses"
        />
        <MetricCard
          label="Cursos publicados"
          value={metrics ? String(metrics.publishedCourses) : '—'}
          to="/teacher/courses"
        />
        <MetricCard
          label="Inscripciones activas"
          value={metrics ? String(metrics.activeEnrollments) : '—'}
          to="/teacher/earnings"
        />
        <MetricCard
          label={`Ganado en ${dashboard?.period.label ?? 'el periodo'}`}
          value={metrics ? formatMoney(metrics.earnedInPeriod, currency) : '—'}
          to="/teacher/earnings"
          accent="#3f7410"
        />
        <MetricCard
          label="Pendiente"
          value={metrics ? formatMoney(metrics.pending, currency) : '—'}
          to="/teacher/earnings"
          accent="#b8770f"
        />
        <MetricCard
          label="Avance promedio"
          value={
            metrics?.averageCompletionRate === null || metrics === undefined
              ? 'Sin datos'
              : `${Math.round(metrics.averageCompletionRate * 100)}%`
          }
          to="/teacher/courses"
        />
      </MetricGrid>

      {dashboard && (
        <p style={{ color: '#666', fontSize: 12, marginTop: 0, marginBottom: 24 }}>
          Las cifras del periodo cubren {formatDay(dashboard.period.from)} a{' '}
          {formatDay(dashboard.period.to)}. El resto es acumulado.
        </p>
      )}

      {dashboard && dashboard.attentionIncomplete.length > 0 && (
        // The server could not read one of the sources this list is built
        // from. Saying so matters: an item missing because a query failed
        // looks exactly like an item missing because there is nothing to do,
        // and the second is the one that makes a teacher stop checking.
        <p style={{ color: '#7a4d06', fontSize: 13, marginBottom: 8 }}>
          Esta lista puede estar incompleta: no pudimos revisar{' '}
          {dashboard.attentionIncomplete.map((s) => SOURCE_LABELS[s] ?? s).join(' y ')}.
        </p>
      )}

      {attention.length > 0 && (
        <section style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 18, marginBottom: 10 }}>Requiere tu atención</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {attention.map((item) => (
              <div
                key={item.kind}
                style={{
                  border: '1px solid #f5a623',
                  background: '#fff8e6',
                  borderRadius: 8,
                  padding: '12px 14px',
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ flex: '1 1 260px', color: '#7a4d06', fontSize: 14 }}>
                  {describeAttention(item, currency)}
                </span>
                <Link
                  to={ATTENTION_ROUTE[item.kind].to}
                  className="btn btn-primary btn-sm"
                  style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
                >
                  {ATTENTION_ROUTE[item.kind].cta}
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}

      <section style={{ marginBottom: 24 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 10,
            flexWrap: 'wrap',
          }}
        >
          <h2 style={{ fontSize: 18, margin: 0, flex: '1 1 auto' }}>Tus cursos</h2>
          <Link to="/teacher/courses" style={{ fontSize: 14 }}>
            Ver todos
          </Link>
        </div>

        {!dashboard ? null : dashboard.courses.length === 0 ? (
          <p style={{ color: '#666' }}>
            Aún no tienes cursos. <Link to="/teacher/courses/new">Crea el primero</Link>.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {dashboard.courses.slice(0, 5).map((c) => (
              <Link
                key={c.courseId}
                to="/teacher/courses"
                style={{
                  border: '1px solid #d1d5da',
                  borderRadius: 8,
                  padding: '12px 14px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 10,
                  alignItems: 'center',
                  textDecoration: 'none',
                  color: 'inherit',
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ fontWeight: 600 }}>{c.title}</span>
                <span style={{ fontSize: 13, color: '#666' }}>
                  {c.learners} {c.learners === 1 ? 'alumno' : 'alumnos'}
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>

      {dashboard && dashboard.unavailable.length > 0 && (
        <section>
          <h2 style={{ fontSize: 15, marginBottom: 6, color: '#666' }}>Todavía no disponible</h2>
          <ul style={{ color: '#666', fontSize: 13, margin: 0, paddingLeft: 18 }}>
            {dashboard.unavailable.map((u) => (
              <li key={u.metric} style={{ marginBottom: 4 }}>
                {METRIC_LABELS[u.metric] ?? u.metric}
              </li>
            ))}
          </ul>
        </section>
      )}
    </PortalPage>
  );
};

/**
 * Names for the panels the platform cannot fill yet.
 *
 * Shown rather than hidden. A panel that exists without data is how the
 * hardcoded "3 mensajes" badge got shipped; saying what is missing costs one
 * line and cannot be mistaken for a figure.
 */
/** Names for the sources the attention list reads. */
const SOURCE_LABELS: Record<string, string> = {
  availability: 'tu disponibilidad',
  bookings: 'tus horarios agendados',
};

/** Where each item sends the teacher, and what the button says. */
const ATTENTION_ROUTE: Record<AttentionItem['kind'], { to: string; cta: string }> = {
  SESSION_SOON: { to: '/teacher/calendar', cta: 'Ver agenda' },
  BOOKING_AWAITING_YOU: { to: '/teacher/calendar', cta: 'Revisar horarios' },
  EVIDENCE_PENDING: { to: '/teacher/courses', cta: 'Completar evidencia' },
  DISPUTE_OPEN: { to: '/teacher/earnings', cta: 'Ver ingresos' },
  NO_AVAILABILITY: { to: '/teacher/availability', cta: 'Definir horarios' },
  NO_COURSES: { to: '/teacher/courses/new', cta: 'Crear curso' },
  DRAFTS_UNPUBLISHED: { to: '/teacher/courses', cta: 'Revisar borradores' },
};

/**
 * What each item says, in the teacher's own terms.
 *
 * The copy lives here rather than on the server because it is presentation:
 * the server says what is true, the screen says it in words. EVIDENCE_PENDING
 * names the amount on purpose — "three sessions need a rubric" is a chore,
 * "USD 21.42 of your money is waiting on three rubrics" is a reason.
 */
const describeAttention = (item: AttentionItem, currency?: string): string => {
  const n = item.count ?? 0;
  switch (item.kind) {
    case 'SESSION_SOON':
      return item.at
        ? `Tienes una sesión a las ${new Date(item.at).toLocaleTimeString('es-MX', {
            hour: '2-digit',
            minute: '2-digit',
          })}.`
        : 'Tienes una sesión próxima.';
    case 'BOOKING_AWAITING_YOU':
      return n === 1
        ? 'Una familia propuso un horario y espera tu confirmación.'
        : `${n} familias propusieron horarios y esperan tu confirmación.`;
    case 'EVIDENCE_PENDING':
      return `${formatMoney(
        item.amount,
        currency,
      )} no se pueden liberar hasta que completes la evidencia de ${n} ${
        n === 1 ? 'sesión' : 'sesiones'
      }.`;
    case 'DISPUTE_OPEN':
      return `Tienes ${formatMoney(item.amount, currency)} en disputa.`;
    case 'NO_AVAILABILITY':
      return 'No has definido tu disponibilidad, así que nadie puede agendar contigo.';
    case 'NO_COURSES':
      return 'Todavía no has creado ningún curso.';
    case 'DRAFTS_UNPUBLISHED':
      return `Tienes ${n} ${n === 1 ? 'curso' : 'cursos'} en borrador que nadie puede ver todavía.`;
  }
};

const METRIC_LABELS: Record<string, string> = {
  studentsLiveToday: 'Alumnos en sesión hoy',
  pendingReviews: 'Reseñas por responder',
  recentMessages: 'Mensajes recientes',
  studentActivity: 'Actividad de alumnos',
};

const formatDay = (iso: string): string =>
  new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });

export default TeacherDashboard;
